'use strict'

const {
  section, naming, saving, deleteConfirm, columnTypes, relationshipTypes,
  imageResize, multiselectColumn, valueFormatting, formulaColumn, columnWriteRules, uniqueConstraint
} = require('./sections')
const { BULK_MATCH_PREVIEW_LIMIT } = require('../bulk-match-preview')

const createSpreadsheet = {
  title: 'Create a spreadsheet',
  intro: 'A spreadsheet is the table that stores records. Pages and views read it. This dialog creates an empty sheet, or asks the AI to create the sheet and a useful set of columns.',
  sections: [
    section('Manual', [
      'Spreadsheet title is the name in the spreadsheet list. Unique title is the identifier used by views, relationships, the API, and the database file\'s tables. It must start with a letter or an underscore, then letters, digits, and underscores.',
      'The unique title fills from the title until you edit it. OK on this tab creates the sheet with the system columns id, created time, and updated time. Add your own columns after that.',
      'The sheet is created in the app that is open in the designer. It is not shared with other apps.'
    ]),
    section('With AI', [
      'Describe the records you want to keep. Name the columns in the description when you care about them. The AI picks a title and column types from that description.',
      'The confirm button reads create with AI on this tab. It can take up to a minute. The token line is this account\'s usage for the billing period. An error stays in the dialog so you can revise the description.',
      'Review the columns after the sheet opens. Change a type in edit column if the AI picked text for a value that should be a date, a relationship, or a number.'
    ]),
    saving('OK', 'the new spreadsheet')
  ]
}

const copySpreadsheet = {
  title: 'Copy a spreadsheet',
  intro: 'Copy makes a new spreadsheet from the one that is open. The original is not changed.',
  sections: [
    section('Name of the copy', [
      'New spreadsheet title and unique title name the copy. The unique title has to be free in this app and has to start with a letter or an underscore, then letters, digits, and underscores.',
      'The unique title starts from the title until you edit it. It cannot match the original sheet\'s unique title.'
    ]),
    section('Records', [
      'Copy records duplicates every row. Relationship values on relationship columns of this spreadsheet are copied with the rows, so the new rows point at the same related records.',
      'Leave the checkbox off to copy only the columns and their settings. The new sheet is empty. Formulas, choices, and relationship definitions are part of the structure and are copied either way.',
      'Copying records does not copy rows of other spreadsheets. A relationship still points at the original related sheet.'
    ]),
    saving('copy', 'the new spreadsheet')
  ]
}

const deleteSpreadsheet = {
  title: 'Delete a spreadsheet',
  intro: 'This dialog removes the spreadsheet that is open, including its columns and its records.',
  sections: [
    deleteConfirm('spreadsheet'),
    section('What else breaks', [
      'Views that read this spreadsheet lose their data source. Relationships on other sheets that point here lose their target. Automations and schedules that write this sheet fail on the next run.',
      'Type the spreadsheet title exactly, matching the title shown in the question, before you confirm. The records cannot be restored from this dialog.'
    ]),
    saving('delete', 'the removal of this spreadsheet')
  ]
}

const createColumn = {
  title: 'Create a column',
  intro: 'A column is one field on every record of this spreadsheet. This dialog adds the field. It does not add the field to forms or tables until you place it on those views.',
  sections: [
    section('Name', [
      'Column title is the label in the grid and the default label on a form. Unique title is the identifier stored in each record and referenced by views, formulas, filters, and automations. It must start with a letter or an underscore, then letters, digits, and underscores.',
      'The unique title fills from the title until you edit it. Renaming it later does not rewrite views that already stored the old identifier.'
    ]),
    columnTypes(),
    imageResize(),
    relationshipTypes(),
    multiselectColumn(),
    section('What is not in this dialog', [
      'Required, description, a default value, unique values, value formatting, and the formula editor are on edit column, after the column exists. Create the column first, then open it again to set those.',
      'System columns id, created time, and updated time already exist. You do not create them here.'
    ]),
    saving('OK', 'the new column onto the spreadsheet')
  ]
}

const editColumn = {
  title: 'Edit a column',
  intro: 'This dialog changes one column on the open spreadsheet: its name, its type, and the options that belong to that type.',
  sections: [
    section('Name and type', [
      'Column title is the label. Unique title is the identifier in records, views, formulas, and filters. Changing the unique title does not rewrite those references.',
      'Column type changes how new values are read and written. Changing a column that already has data can make old values display blank or fail validation when they do not fit the new type. Prefer a new column when the existing values must stay as they are.'
    ]),
    columnTypes(),
    relationshipTypes(),
    imageResize(),
    multiselectColumn(),
    formulaColumn(),
    columnWriteRules(),
    uniqueConstraint(),
    valueFormatting(),
    section('Delete', [
      'Delete column opens the confirm dialog. It does not delete the column immediately. You still type the column title there. Deleting a column removes its values from every record and removes a relationship\'s links.'
    ]),
    saving('OK', 'the column')
  ]
}

const addColumnsAi = {
  title: 'Add columns with AI',
  intro: 'This dialog adds columns to the spreadsheet that is already open. It does not create a new spreadsheet.',
  sections: [
    section('Description', [
      'The label names the spreadsheet the columns will be added to. Describe each column you want. Mention the kind of value when it matters: a phone number, a date, an address, a yes or no, a link to another sheet.',
      'The AI chooses the title, the unique title, and the type. It will not delete columns that are already there.',
      'The confirm button reads create with AI. It can take up to a minute. The token line is this account\'s usage for the billing period. An error or a success message stays in the dialog.',
      'After the columns appear, open any column whose type should be a relationship, a formula, or a multiselect with a specific list of choices. The description can ask for those, and you should still check the result.'
    ]),
    saving('create with AI', 'the generated columns')
  ]
}

const deleteColumn = {
  title: 'Delete a column',
  intro: 'This dialog removes one column and the values stored in it.',
  sections: [
    deleteConfirm('column'),
    section('Effects', [
      'Every record loses this field. A view that showed the column keeps a reference to an identifier that no longer exists until you remove that column from the view.',
      'A formula that used this column will fail or show blank when it is recalculated. A relationship column also drops the links in its bridge table.',
      'Type the column title exactly. The system columns id, created time, and updated time are not deleted from this dialog.'
    ]),
    saving('delete', 'the removal of this column')
  ]
}

const addRow = {
  title: 'Add a new row',
  intro: 'This dialog creates one record on the open spreadsheet. Each field is a column. System columns are filled for you.',
  sections: [
    section('Fields', [
      'Text is a single line. Password is hidden while you type and is stored as a hash. Number accepts digits. Date time uses the local date and time control.',
      'Image accepts an image file. File accepts any file. The chosen filename is shown under the control. Multiselect shows the choices defined on the column. A single-select column keeps one value. A multiple-select column keeps every value you check. A multiselect with no choices falls back to a text box, and comma-separated text is the value.',
      'Relationship shows the records already linked, a search box, and results. Click a result to link it. The x on a tag unlinks it before you save. The search looks up records on the related spreadsheet.',
      'Formula columns are not edited here. They calculate. A spreadsheet with no editable columns shows that message instead of a form.',
      'Leave a field blank to store it empty. Required checks that belong to a form are not applied in this dialog. Unique columns reject the save when another row already has the same value.'
    ]),
    saving('OK', 'the new record')
  ]
}

const deleteRows = {
  title: 'Delete rows',
  intro: 'This dialog deletes the records whose checkboxes are selected in the grid.',
  sections: [
    section('What is deleted', [
      'The sentence states how many records will be removed. The delete cannot be undone from this dialog. History keeps an entry for the delete, but the record itself is gone.',
      'Relationships that pointed at these records lose those links. A view filtered to these records shows an empty set after the refresh.',
      'OK is labeled delete. The button is disabled while the delete is running so a second click does not send the request twice.'
    ]),
    saving('delete', 'the selected records')
  ]
}

const bulkActions = {
  title: 'Bulk actions',
  intro: 'Bulk actions starts with three choices. Update values writes fields onto records. Run a step workflow starts a named workflow. Run an automation starts one automation that uses this spreadsheet. Checked rows choose the records, or a filter does when nothing is checked.',
  sections: [
    section('Choice', [
      'The first screen has three buttons. Update values opens the filter and the value boxes. Run a step workflow opens the workflows that begin on this spreadsheet. Run an automation opens the automations that write these records, save a response onto the selected record, or fill a placeholder from a field here.',
      'Back, at the top of the next screen and in the footer, returns to that choice. Cancel closes the dialog. The three actions do not run together.'
    ]),
    section('Filter', [
      'The filter builder is the same control visitors use. Add a condition with a field, an operator, and a value. All means every condition must match. Any means one matching condition is enough.',
      `The line under the builder counts the records that match. Under that count, a table lists those records with the same columns as the spreadsheet, so you can see which rows the update will touch. When more than ${BULK_MATCH_PREVIEW_LIMIT} records match, the table shows the first ${BULK_MATCH_PREVIEW_LIMIT} and says how many were left out.`,
      'No filter set means every record on the spreadsheet will be updated, and those records are listed in the same table. Read the count and the rows before you apply.',
      'The count and the rows are the match at the moment they were loaded. Records created by someone else after that are not in the list you saw.'
    ]),
    section('Step workflow', [
      'Each workflow is a card with its name, its id, and its description when it has one. Click a card to choose it. A green edge marks the one that will run. When this spreadsheet has only one workflow, that card is already chosen.',
      'Checked rows are the records that start the workflow, and those rows are listed under Records. One checked row is enough. If no rows are checked, add a filter. Run workflow stays disabled until a workflow is chosen and at least one record is included. It never runs on every record just because the filter is empty.',
      'Run workflow is the footer button on this screen. It does not write the value boxes. Each record gets its own run. A line under the cards says how many ran and how many failed. At most 100 records are included.'
    ]),
    section('Automation', [
      'Each listed automation is a card with its name, its type, and a line that says how it uses the selected records. Click a card to choose it. A green edge marks the one that will run. When only one automation applies, that card is already chosen.',
      'An automation is listed when it writes this spreadsheet, saves its response onto the triggering record, or uses a field from this spreadsheet in a placeholder. A password, image, or file is not copied into a placeholder. A webhook is not listed, because a webhook waits for an outside call.',
      'Run automation is the footer button on this screen. Each selected record is sent with the run, and placeholders that match a field are filled from that record. The same record rules as a workflow apply: checked rows, or a filter, and never every record just because the filter is empty. At most 100 records are included.'
    ]),
    section('Values', [
      'Only boxes you fill are written. An empty box leaves the existing value on each record. The placeholder says that.',
      'The controls match the column type: text, password, number, date time, image, file, and multiselect. A multiselect with choices uses the choice control. Without choices it is a text box, and commas separate values. A multiselect can instead append or remove one value. A relationship can append or remove one record id.',
      'Password values entered here are stored as passwords. Image and file replace the file on every matching record with the one file you pick.',
      'Apply stays disabled until at least one value is filled, and while the update is running. A success or error alert appears at the bottom of the dialog.'
    ]),
    saving('apply', 'the filled values onto the matching records')
  ]
}

const importSpreadsheet = {
  title: 'Import a spreadsheet',
  intro: 'Import creates a spreadsheet from a CSV file. The first step reads the file. The second step names the sheet and chooses columns.',
  sections: [
    section('File', [
      'Choose a CSV file. The first row is treated as the column titles. Later rows are records. After the file is read, the form switches to the name and column list.',
      'Choose different file clears that parse and returns to the file picker. Nothing is written until you confirm import.'
    ]),
    section('Name and columns', [
      'Spreadsheet title and unique title name the new sheet. The unique title must be free and must start with a letter or an underscore, then letters, digits, and underscores. It fills from the title until you edit it.',
      'Each detected column is a checkbox, a name, and a guessed type. Uncheck a column to leave it out of the new sheet. The heading counts how many are selected. At least one column has to stay selected.',
      'The row count is how many data rows will be imported. Import creates the sheet and those rows. To add or update rows on the spreadsheet that is open, use import rows.'
    ]),
    saving('import', 'the new spreadsheet and its rows')
  ]
}

const importRows = {
  title: 'Import rows',
  intro: 'Import rows maps a CSV onto the spreadsheet that is open. It can insert new records and update records that match a key. Nothing is written until you confirm the preview.',
  sections: [
    section('File and columns', [
      'Choose a CSV file. The first row is the column titles. Each file column maps onto a column of this spreadsheet, or onto Skip. A column can be mapped once. The match uses the column title or its unique title.',
      'Match existing rows by chooses the key. When that value matches one record, the row updates that record. When it matches none, the row is inserted. When it matches more than one, the row fails. Do not match inserts every row.',
      'A relationship column matches the related record by the label its display formula shows. Several labels, separated by commas, are allowed when the relationship can hold more than one record.'
    ]),
    section('Preview', [
      'Preview counts the rows that will be inserted, the rows that will be updated, and the rows that fail. A failed row is not saved. The message is the validation rule, a missing relationship label, or a key that matches more than one record.',
      'Download failed rows saves that list as a CSV, with the row number, the message, and the cells from the file.',
      'Date and time columns use the file time zone. The import stops when that zone is missing. Load rows that fail a validation rule is off until you check it for this run. A relationship that does not match is still not saved.',
      'Import writes the inserts and the updates. The spreadsheet you were looking at reloads.'
    ]),
    saving('import', 'the inserted and updated rows')
  ]
}

const recordHistory = {
  title: 'Record history',
  intro: 'History is the log of creates, updates, deletes, and relationship link changes for this spreadsheet. This dialog only reads the log. It does not restore a record.',
  sections: [
    section('Rows', [
      'Time is when the change was recorded. Action is create, update, delete, link, or unlink. Record ID is the record the change belongs to. When you opened history from one row, the list is that record. When you opened it for the sheet, the list covers the sheet.',
      'An update lists each changed field, the value before, and the value after. A create lists the values that were written. A delete lists the values that were removed. Link and unlink name the relationship column and the other record\'s id.',
      'An image field shows a small picture of the previous value and the new value when those bytes are in the log. A picture that was not kept shows as image not stored.',
      'A password column shows (hidden) instead of the password. An empty password still shows as empty, so you can see that a password was added or cleared without reading the password itself.',
      'Prev and Next move through pages of the log. The page line shows which page you are on and how many entries exist. There is no OK button. Close the dialog when you are done reading.'
    ])
  ]
}

const editRichTextCell = {
  title: 'Edit rich text on a record',
  intro: 'This dialog edits the rich text stored in one cell. It is the value on the record, not the static rich text view on a page.',
  sections: [
    section('Editor', [
      'The toolbar formats the document: marks, lists, links, and the other controls the editor provides. The document is saved on this record in this column only.',
      'Save writes the cell and closes the dialog. Cancel discards the edits since the dialog opened and leaves the stored document as it was.',
      'Clicking the darkened area around the dialog does not close it, so a click meant for the toolbar does not throw the edit away. Use Cancel or Save.',
      'The grid shows a plain-text preview of the document. Open the cell again to see the formatting.'
    ])
  ]
}

const importQuery = {
  title: 'Import query results',
  intro: 'This dialog saves the current AI query results as a new spreadsheet. It does not append rows to a sheet that already exists.',
  sections: [
    section('Name', [
      'Spreadsheet title is the name in the spreadsheet list. Unique title is the identifier. It must start with a letter or an underscore, then letters, digits, and underscores. It fills from the title until you edit it.',
      'Import stays disabled until both names are filled, and while the import is running.',
      'The columns and rows come from the result set you are looking at. Run the query again before you import if the answer on screen is not the one you want to keep.',
      'After the import, the new spreadsheet is in this app and can be used by views like any sheet you created by hand.'
    ]),
    saving('Import', 'a spreadsheet from these results')
  ]
}

module.exports = {
  'create-spreadsheet': createSpreadsheet,
  'copy-spreadsheet': copySpreadsheet,
  'delete-spreadsheet': deleteSpreadsheet,
  'create-column': createColumn,
  'edit-column': editColumn,
  'add-columns-ai': addColumnsAi,
  'delete-column': deleteColumn,
  'add-row': addRow,
  'delete-rows': deleteRows,
  'bulk-actions': bulkActions,
  'import-spreadsheet': importSpreadsheet,
  'import-rows': importRows,
  'record-history': recordHistory,
  'edit-rich-text-cell': editRichTextCell,
  'import-query-results': importQuery
}
